import { defineStore, storeToRefs } from 'pinia'
import { useDYUserStore, type AvailPatnerAllowedFunctionType } from '~~/modules/nuxt3-module-doyac/stores/user'
import { useCartApi } from '../composables/ig-cart'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useDYAppConfig } from '~/modules/nuxt3-module-doyac/composables/config'
import { useIGCoursePaymentApi, type IGCoursePayment } from '../composables/ig-course-payment'
import { stripTag } from '~/modules/nuxt3-module-doyac/libs/utils'
import { useDYAuth } from '~/modules/nuxt3-module-doyac/composables/auth'
import { $dy__fetchWithToken } from '~/modules/nuxt3-module-doyac/composables/fetch'

interface AvailUpgradeInfo {
  paymentNo: number,
  goodsType: 'course' | 'tour' | 'book' | 'good'
  currentTitle: string,
  currentPrice?: number, // 2026-03-31 마이페이지에서 업그레이드 모달 직접 오픈용
  currentTeacher?: string, // 2026-03-31 마이페이지에서 업그레이드 모달 직접 오픈용
  currentTypedPk?: string, // 2026-03-31 마이페이지에서 업그레이드 모달 직접 오픈용
  currentChildrenCount?: number, // 2026-04-07 현재 상품 구성품 수
  currentChildrenTypedPks?: string[], // 2026-04-07 현재 상품 구성품 typedPk 목록
  upgradePackageData?: any, // 2026-03-31 마이페이지에서 업그레이드 모달 직접 오픈용
  expiry: Dayjs
}

export const useIGUserStore = defineStore(`nuxt3-module-ingang-user`, () => {
  const parent = useDYUserStore()
  const refs = storeToRefs(parent)
  const { getList } = useCartApi()
  const { siteName } = useDYAppConfig()
  const { getList: getListCoursePayment } = useIGCoursePaymentApi()

  const availUpgradeInfo = ref<AvailUpgradeInfo[]>()
  const hasAvailUpgradeInfo = computed(() => {
    if (availUpgradeInfo.value && availUpgradeInfo.value.length > 0) {
      return availUpgradeInfo.value.some((info) => {
        return dayjs().isBefore(info.expiry) || dayjs().isSame(info.expiry)
      })
    } else return false
  })

  const cartInfo = ref<string[]>()

  const player = ref({
    isDownloadable: false,
    strategy: 'WEBBEAT',
    defaultQuality: 'hdplus'
  })

  const sugangCommentReg = ref(false)

  const { isTeacher } = useDYAuth()
  const isCommissionZero = ref(false)

  async function injectUser() {
    const result = await parent.injectUser()
    const { API_BASE_URL } = useDYAppConfig()
    if (result) {
      if(isTeacher.value) {
        try {
          const res = await $dy__fetchWithToken<any>(`${API_BASE_URL}/api/oops-lecturers`, { method: 'get', params: { id: `${parent.id}` } })
          isCommissionZero.value = res?.items?.[0]?.commission === '0'
        } catch(e: any) {
          console.error('injectUser::isTeacher', e)
        }
      }
  
      try{
        await injectCartInfo()
      }catch(e:any){
        console.error("injectUser", e)
      }
    }
    return result
  }

  async function requestLogout() {
    await parent.requestLogout()
    cartInfo.value = []
    availUpgradeInfo.value = []
  }

  const availPartnerAllowedFunction = (functionName: AvailPatnerAllowedFunctionType) => {
    return parent.availPartnerAllowedFunction(functionName)
  }

  /**
   * 로그인 되어 있다면 해당 유저가 등록했던 모든 장바구니 상품의 typedPk를 스토어에 따로 저장
   */
  const injectCartInfo = async () => {
    if (!parent.isSignedIn) return

    // 2026-06-11 관리자 앱(appId가 nuxt3-admin-*)에서는 장바구니 기능 자체가 없으므로
    // /api/carts?getCartInfo=true 호출이 불필요한 네트워크 부하만 유발한다 → 스킵.
    // (DYDataTable.vue의 _isAdminApp 판별과 동일한 패턴. 유저 프로젝트는 기존대로 동작)
    const { appId } = useDYAppConfig()
    if (typeof appId === 'string' && appId.startsWith('nuxt3-admin-')) return

    // 2026-08-19 [버그수정] `edgeCache:'2h'` 제거.
    //   장바구니는 **사용자가 수시로 바꾸는 목록**인데 2시간 엣지 캐시가 걸려 있었다. 담거나 빼도
    //   최대 2시간 동안 옛 목록이 그대로 와서 cartInfo 가 실제와 어긋난다.
    //   실측(2026-08-19): 장바구니에 3건이 있는 계정인데 응답이 `[]` 로 와서
    //     · 이미 담은 상품인데 결제 시트가 [장바구니 바로가기] 대신 [장바구니] 를 노출하고
    //     · 투어팩3/투어팩5 진행률이 0개로 잡혀, 담은 직후 안내바가 "1개" 부터 시작했다.
    //   담기/빼기 시 엣지 태그를 purge 하지 않는 이상 캐시가 성립할 수 없는 데이터라 캐시 자체를 뺀다.
    //   ⚠️ 공유모듈이라 닷컴/아트에도 적용된다 — 같은 증상을 겪고 있었으므로 그쪽도 교정된다.
    //      비용은 로그인 세션당 인증 GET 1회로, 정확도를 포기할 만한 크기가 아니다.
    const response = await getList({ getCartInfo: true, memberNo: parent.userNo })
    const result: string[] = []

    if (response) {
      response.forEach((item: any) => {
        result.push(item.typedPk)
      })
    }
    
    if (result.length > 0) {
      cartInfo.value = result
    } else {
      cartInfo.value = []
    }
  }

  function addCartTypedPk(typedPk: string) {
    if (cartInfo.value && cartInfo.value?.indexOf(typedPk) > -1) { //기존에 이미 있으면,

    } else {
      if (!cartInfo.value) alert('장바구니 준비가 되어있지 않습니다. 로그인을 다시 해야할 수 있습니다.')
      cartInfo.value?.push(typedPk)
    }
  }

  /**
   * 2026-08-19 [버그수정] 필터 결과를 **반환만 하고 cartInfo 에 넣지 않아** 아무 일도 하지 않던 함수였다.
   *   ("새로 커밋함" 이라는 원 주석의 의도가 코드에 없었다)
   *   그래서 장바구니에서 상품을 빼도 cartInfo 에는 그대로 남아, 새로고침 전까지
   *   이미 뺀 상품에 [장바구니 바로가기] 가 뜨고 투어팩 진행률 개수도 안 줄었다.
   *   호출처(IGCartPage 삭제 / IGGoodsModal)는 반환값을 쓰지 않으므로 대입으로 바꿔도 영향 없다.
   */
  function minusCartTypedPk(typedPk: string) {
    if (!cartInfo.value) return

    cartInfo.value = cartInfo.value.filter(item => typedPk !== item)
  }

  const injectAvailUpgradeInfo = async () => {
    if (!parent.isSignedIn) return

    try {
      const res = await getListCoursePayment({ money_yn: 'y', sugangIng: 'y', package_parent_no: 0, availUpgradePackage: 'y', abc_gubun: '1', sameTimeViewCount: "{{(2,<=)|(n)}}" })

      // 2026-03-31 마이페이지에서 업그레이드 모달 직접 오픈을 위해 upgradePackageData 등 추가
      availUpgradeInfo.value = res.items.map((item: IGCoursePayment) => {
        return {
          paymentNo: item.no,
          goodsType: 'course',
          currentTitle: stripTag(item.course.title),
          currentPrice: item.course.price,
          currentTeacher: item.course.teacher,
          currentTypedPk: item.course.typedPk,
          currentChildrenCount: item.currentChildrenCount ?? 1, // 2026-04-06 현재 상품 구성품 수
          currentChildrenTypedPks: item.currentChildrenTypedPks ?? [], // 2026-04-06 현재 상품 구성품 typedPk 목록
          upgradePackageData: item.upgradePackageData,
          expiry: dayjs(item.money_day).set('hour', 23).set('minute', 59).set('second', 59).add(7, 'day')
        }
      })
    } catch (e: any) {
      console.error('injectAvailUpgradeInfo', e)
    }
  }

  return {
    ...refs,
    cartInfo,
    player,
    sugangCommentReg,
    availUpgradeInfo,
    hasAvailUpgradeInfo,
    isCommissionZero,
    init: parent.init,
    injectUser,
    requestLogout,
    ready: parent.ready,
    addCartTypedPk,
    minusCartTypedPk,
    injectAvailUpgradeInfo,
    injectCartInfo,
    availPartnerAllowedFunction
  }
})